import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Close, Description, Share } from '@mui/icons-material';
import {
  autorizacaoFazendaService,
  ManejadorAutorizacao,
} from '../services/autorizacaoFazendaService';

const GOV_BR_URL =
  'https://www.gov.br/governodigital/pt-br/identidade/assinatura-eletronica-assinatura-digital';

interface Props {
  open: boolean;
  fazendaId: number | null;
  fazendaNome: string;
  onClose: () => void;
}

const AutorizacaoFazendaDialog: React.FC<Props> = ({ open, fazendaId, fazendaNome, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [proprietarioNome, setProprietarioNome] = useState('');
  const [proprietarioRg, setProprietarioRg] = useState('');
  const [proprietarioCpf, setProprietarioCpf] = useState('');
  const [cidadeEmissao, setCidadeEmissao] = useState('');
  const [manejadores, setManejadores] = useState<ManejadorAutorizacao[]>([]);
  const [socios, setSocios] = useState<{ id: number; nome_completo: string; cpf: string; cr?: string }[]>([]);
  const [socioAddId, setSocioAddId] = useState<number | ''>('');
  const [manualNome, setManualNome] = useState('');
  const [manualCpf, setManualCpf] = useState('');
  const [manualCr, setManualCr] = useState('');
  const [geradaId, setGeradaId] = useState<number | null>(null);
  const [telefoneProprietario, setTelefoneProprietario] = useState('');

  useEffect(() => {
    if (!open || !fazendaId) return;
    setLoading(true);
    setError('');
    setSuccess('');
    setGeradaId(null);
    autorizacaoFazendaService
      .preenchimento(fazendaId)
      .then((data) => {
        setProprietarioNome(data.proprietario_nome || '');
        setProprietarioRg(data.proprietario_rg || '');
        setProprietarioCpf(data.proprietario_cpf || '');
        setCidadeEmissao(data.cidade_emissao || data.cidade || '');
        setTelefoneProprietario(data.telefone_proprietario || '');
        setManejadores(data.manejadores_sugeridos?.length ? [...data.manejadores_sugeridos] : []);
        setSocios(data.socios_para_manejador || []);
      })
      .catch(() => setError('Erro ao carregar dados da autorização'))
      .finally(() => setLoading(false));
  }, [open, fazendaId]);

  const addSocio = () => {
    if (!socioAddId) return;
    const s = socios.find((x) => x.id === socioAddId);
    if (!s) return;
    if (manejadores.some((m) => m.socio_id === s.id || m.cpf === s.cpf)) {
      setError('Este manejador já está na lista');
      return;
    }
    setManejadores([...manejadores, { nome: s.nome_completo, cpf: s.cpf, cr: s.cr || '', socio_id: s.id }]);
    setSocioAddId('');
    setError('');
  };

  const addManual = () => {
    if (!manualNome.trim()) {
      setError('Informe o nome do manejador');
      return;
    }
    setManejadores([
      ...manejadores,
      { nome: manualNome.trim(), cpf: manualCpf.trim(), cr: manualCr.trim() },
    ]);
    setManualNome('');
    setManualCpf('');
    setManualCr('');
    setError('');
  };

  const removeManejador = (index: number) => {
    if (manejadores.length <= 1) {
      setError('É necessário ao menos um manejador');
      return;
    }
    setManejadores(manejadores.filter((_, i) => i !== index));
  };

  const handleGerar = async () => {
    if (!fazendaId) return;
    if (!proprietarioNome.trim()) {
      setError('Informe o nome do proprietário que assina');
      return;
    }
    try {
      setSaving(true);
      setError('');
      const res = await autorizacaoFazendaService.gerar(fazendaId, {
        proprietario_nome: proprietarioNome.trim(),
        proprietario_rg: proprietarioRg.trim() || undefined,
        proprietario_cpf: proprietarioCpf.trim() || undefined,
        manejadores,
        cidade_emissao: cidadeEmissao.trim() || undefined,
      });
      setGeradaId(res.id);
      setSuccess(
        'PDF gerado. Envie ao proprietário para assinar. Após assinado, faça upload em Documentos → Autorização Fazenda.',
      );
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao gerar autorização');
    } finally {
      setSaving(false);
    }
  };

  const abrirPdf = async () => {
    if (!geradaId) return;
    try {
      const blob = await autorizacaoFazendaService.baixarPdf(geradaId);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      setError('Não foi possível abrir o PDF');
    }
  };

  const compartilharWhatsApp = () => {
    const digits = telefoneProprietario.replace(/\D/g, '');
    const msg = encodeURIComponent(
      `Olá! Segue a autorização de acesso à propriedade ${fazendaNome} para manejo de javali. ` +
        'Por favor, assine pelo Gov.br ou reconheça firma em cartório.',
    );
    if (digits) {
      window.open(`https://wa.me/55${digits}?text=${msg}`, '_blank', 'noopener,noreferrer');
    } else {
      window.open(GOV_BR_URL, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Autorização de caça — {fazendaNome}</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              O PDF gerado é enviado ao proprietário para assinar. Após assinado, faça upload em{' '}
              <strong>Documentos → Autorização Fazenda</strong>.
            </Typography>

            <Typography variant="subtitle2" gutterBottom>
              Proprietário (quem assina)
            </Typography>
            <TextField
              fullWidth
              label="Nome completo"
              margin="dense"
              value={proprietarioNome}
              onChange={(e) => setProprietarioNome(e.target.value)}
              required
            />
            <Box display="flex" gap={2} mt={1}>
              <TextField
                fullWidth
                label="RG"
                value={proprietarioRg}
                onChange={(e) => setProprietarioRg(e.target.value)}
              />
              <TextField
                fullWidth
                label="CPF"
                value={proprietarioCpf}
                onChange={(e) => setProprietarioCpf(e.target.value)}
              />
            </Box>
            <TextField
              fullWidth
              label="Cidade da assinatura"
              margin="dense"
              sx={{ mt: 1 }}
              value={cidadeEmissao}
              onChange={(e) => setCidadeEmissao(e.target.value)}
            />

            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
              Manejadores
            </Typography>
            {manejadores.map((m, idx) => (
              <Box
                key={`${m.cpf}-${idx}`}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                sx={{ py: 0.5, borderBottom: '1px solid #eee' }}
              >
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {m.nome}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    CPF {m.cpf || '—'} · CR {m.cr || '—'}
                  </Typography>
                </Box>
                {manejadores.length > 1 && (
                  <IconButton size="small" onClick={() => removeManejador(idx)}>
                    <Close fontSize="small" />
                  </IconButton>
                )}
              </Box>
            ))}

            <Box display="flex" gap={1} alignItems="center" mt={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Adicionar associado</InputLabel>
                <Select
                  label="Adicionar associado"
                  value={socioAddId === '' ? '' : String(socioAddId)}
                  onChange={(e) => setSocioAddId(e.target.value ? Number(e.target.value) : '')}
                >
                  {socios.map((s) => (
                    <MenuItem key={s.id} value={String(s.id)}>
                      {s.nome_completo}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button variant="outlined" startIcon={<Add />} onClick={addSocio}>
                Add
              </Button>
            </Box>

            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
              Ou cadastre manualmente:
            </Typography>
            <Box display="flex" gap={1} mt={1} flexWrap="wrap">
              <TextField size="small" label="Nome" value={manualNome} onChange={(e) => setManualNome(e.target.value)} />
              <TextField size="small" label="CPF" value={manualCpf} onChange={(e) => setManualCpf(e.target.value)} />
              <TextField size="small" label="CR" value={manualCr} onChange={(e) => setManualCr(e.target.value)} />
              <Button size="small" onClick={addManual}>
                Incluir
              </Button>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 3, pb: 2 }}>
        <Button onClick={onClose}>Fechar</Button>
        {!geradaId ? (
          <Button variant="contained" onClick={handleGerar} disabled={saving || loading}>
            {saving ? 'Gerando...' : 'Gerar PDF'}
          </Button>
        ) : (
          <>
            <Button variant="outlined" startIcon={<Description />} onClick={abrirPdf}>
              Abrir PDF
            </Button>
            <Button variant="outlined" startIcon={<Share />} onClick={compartilharWhatsApp}>
              Enviar p/ assinar
            </Button>
            <Button
              variant="contained"
              href={GOV_BR_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Gov.br
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AutorizacaoFazendaDialog;
